import { Agent, AgentSettings, AgentStatsSummary, ExecutionLog, TestResult } from '../../types/agent-types-new';
import { AgentLog, AgentTestInput, AgentTestResult } from '../../types/agent-types';
import { classifyExecutionStatus } from '../agent-execution-status';
import { VoiceSettings } from '../../components/agents/voice-types';
import { agentApiFixed } from './agent-api-fixed';
import { agentMappings, getImplementedAgentIds, getComingSoonAgentIds } from './agent-mappings';
import { crewAgents } from '../../data/crew-agents';
import { API_URL, API_BASE_URL as CONFIG_API_BASE_URL } from '../../config/api';
import { authFetch } from '../../utils/auth-fetch';
import { AGENT_ORDER, MISSING_AGENTS, AGENT_ID_MAPPING } from './agent-order-config';

/**
 * Columns agent_logs carries that the AgentLog interface does not declare (live
 * schema on db2, 2026-09-17: trace_id, payload, response, duration_ms, status,
 * error). mapToExecutionLog reads both spellings and prefers the AgentLog one.
 */
type AgentLogRowFields = {
  id?: string;
  trace_id?: string;
  payload?: any;
  duration_ms?: number | null;
  response?: any;
  status?: string | null;
  error?: string | null;
};

const API_BASE_URL = CONFIG_API_BASE_URL;
// Ensure the image path works in both development and Docker environments
const IMAGE_BASE_PATH = '/crew-agents/';

// For debugging - log API requests
const debugMode = true;
const debugLog = (message: string, data?: any) => {
  if (debugMode) {
    console.log(`[AgentService] ${message}`, data || '');
  }
};

/**
 * Maps agent types from the backend to the new agent categories
 * Include marketplace status: 'available', 'coming-soon', or 'hidden'
 * Based on Meet the Crew list and implemented backend services
 */
const agentTypeToCategory: Record<string, {
  category: Agent['category'];
  color: Agent['color'];
  maturity: Agent['maturity'];
  status: 'available' | 'coming-soon' | 'hidden';
  displayName?: string;
}> = {
  // IMPLEMENTED BACKEND SERVICES - MAP TO AVAILABLE AGENTS
  
  // Page role (not directly in Meet the Crew)
  'page-role': { 
    category: 'SEO', 
    color: 'green',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Semantic SEO Optimizer'
  },
  
  // Page Semantics Learner
  'page-semantic-intent': { 
    category: 'SEO', 
    color: 'green',
    maturity: 'VP',
    status: 'available',
    displayName: 'Page Semantics Learner'
  },
  
  // Customer Intent Learner
  'customer-intent': { 
    category: 'Intent', 
    color: 'purple',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Customer Intent Learner'
  },
  
  // Suggestions with Answer
  'question-suggestion': { 
    category: 'Content', 
    color: 'blue',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Suggestions with Answer'
  },
  
  // Adjacent Topics Explorer
  'adjacent-topics': { 
    category: 'Content', 
    color: 'blue',
    maturity: 'VP',
    status: 'available',
    displayName: 'Adjacent Topics Explorer'
  },
  
  // Competitors Analyst
  'competitive-analysis': { 
    category: 'Research', 
    color: 'yellow',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Competitors Analyst'
  },
  
  // ChatBot
  'page-chatbot': { 
    category: 'Support', 
    color: 'teal',
    maturity: 'Senior',
    status: 'available',
    displayName: 'ChatBot'
  },
  
  // Site Crawler
  'site-crawler': { 
    category: 'Research', 
    color: 'yellow',
    maturity: 'VP',
    status: 'available',
    displayName: 'Site Crawler'
  },
  
  // VoiceBot
  'voice-assistant': { 
    category: 'Support', 
    color: 'teal',
    maturity: 'Senior',
    status: 'available',
    displayName: 'VoiceBot'
  },
  
  // Voice Chatbot (separate implementation)
  'voice-chatbot': { 
    category: 'Support', 
    color: 'teal',
    maturity: 'VP',
    status: 'available',
    displayName: 'Voice Chatbot'
  },
  
  // Page Narrator
  'narrative-presentation': { 
    category: 'Content', 
    color: 'blue',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Page Narrator'
  },
  
  // Benchmark Analyst
  'auto-evaluator': { 
    category: 'Analytics', 
    color: 'orange',
    maturity: 'Rookie',
    status: 'available',
    displayName: 'Benchmark Analyst'
  },
  
  // Content Recommender
  'intent-based-recommender': { 
    category: 'Intent', 
    color: 'purple',
    maturity: 'Rookie',
    status: 'available',
    displayName: 'Content Recommender'
  },
  
  // Style Suggestion (not directly in Meet the Crew)
  'style-suggestion': { 
    category: 'Usability', 
    color: 'red',
    maturity: 'Rookie',
    status: 'available',
    displayName: 'Style Suggestion'
  },
  
  // COMING SOON AGENTS - NOT IMPLEMENTED YET
  
  // Web Analyst
  'web-analyst': { 
    category: 'Analytics', 
    color: 'orange',
    maturity: 'VP',
    status: 'available',
    displayName: 'Web Analyst'
  },
  
  // Counter Narrative Builder
  'counter-narrative': { 
    category: 'Content', 
    color: 'blue',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Counter Narrative Builder'
  },
  
  // Hands-off Navigation
  'hands-off-navigation': { 
    category: 'Usability', 
    color: 'red',
    maturity: 'Rookie',
    status: 'available',
    displayName: 'Hands-off Navigation'
  },
  
  // Bug Finder
  'bug-finder': { 
    category: 'Usability', 
    color: 'red',
    maturity: 'Rookie',
    status: 'coming-soon',
    displayName: 'Bug Finder'
  },
  
  // Topic Research Agent
  'topic-research': { 
    category: 'Research', 
    color: 'yellow',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Topic Research Agent'
  },
  
  // MISSING AGENTS - NOW AVAILABLE
  'product-comparison': { 
    category: 'Content', 
    color: 'blue',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Product Comparison Agent'
  },
  'customer-surveys': { 
    category: 'Research', 
    color: 'yellow',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Know Your Customer Surveys Agent'
  },
  'replay-analyst': { 
    category: 'Usability', 
    color: 'red',
    maturity: 'VP',
    status: 'available',
    displayName: 'Replay Analyst: Friction Finder'
  },
  'behavior-analyst': { 
    category: 'Analytics', 
    color: 'orange',
    maturity: 'VP',
    status: 'available',
    displayName: 'Behavior Analyst'
  },
  'sector-assigner': { 
    category: 'SEO', 
    color: 'green',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Sector Assigner'
  },
  
  // NEW AGENTS
  'adjacent-topics-suggestor': { 
    category: 'Content', 
    color: 'blue',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Adjacent Topics Suggestor'
  },
  'growth-analyst': { 
    category: 'Analytics', 
    color: 'orange',
    maturity: 'VP',
    status: 'available',
    displayName: 'Growth Analyst'
  },
  'segment-research': { 
    category: 'Research', 
    color: 'yellow',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Segment Research Agent'
  },
  'customer-segmentation': { 
    category: 'Analytics', 
    color: 'orange',
    maturity: 'VP',
    status: 'available',
    displayName: 'Customer Segmentation Agent'
  },
  'sector-topic-generator': { 
    category: 'Content', 
    color: 'blue',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Sector Topic Generator'
  },
  'site-sector-classifier': { 
    category: 'SEO', 
    color: 'green',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Site Sector Classifier'
  },
  'site-sector-analyzer': { 
    category: 'SEO', 
    color: 'green',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Site Sector Analyzer'
  },
  
  // CHATBOT ANALYTICS AGENTS
  'chatbot-intent-classifier': {
    category: 'Analytics',
    color: 'orange',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Chatbot Intent Classifier'
  },
  'chatbot-quality-scorer': {
    category: 'Analytics',
    color: 'orange',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Chatbot Quality Scorer'
  },
  'chatbot-content-gap-detector': {
    category: 'Analytics',
    color: 'orange',
    maturity: 'VP',
    status: 'available',
    displayName: 'Chatbot Content Gap Detector'
  },
  'chatbot-conversation-flow-analyzer': {
    category: 'Analytics',
    color: 'orange',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Chatbot Conversation Flow Analyzer'
  },
  'chatbot-analytics-agent': {
    category: 'Analytics',
    color: 'orange',
    maturity: 'VP',
    status: 'available',
    displayName: 'Chatbot Analytics Orchestrator'
  },
  
  // HIDDEN AGENTS - DEV/TEST/INACTIVE
  'mock-page-chatbot': { 
    category: 'Support', 
    color: 'teal',
    maturity: 'Rookie',
    status: 'hidden'
  },
  'keyword-extractor': { 
    category: 'SEO', 
    color: 'green',
    maturity: 'Rookie',
    status: 'available',
    displayName: 'Keyword Extractor Agent'
  }
};

/**
 * Maps agent types to avatar and action video paths
 * Uses the Meet the Crew image paths for consistent naming
 */
const agentTypeToMedia: Record<string, {
  avatar: string;
  actionVideo: string;
}> = {
  // IMPLEMENTED AGENTS (Available)
  'page-role': { 
    avatar: `${IMAGE_BASE_PATH}page-role.jpg`, // Use the correct image from Meet the Crew
    actionVideo: `${IMAGE_BASE_PATH}page-role-action.mp4`
  },
  'page-semantic-intent': { 
    avatar: `${IMAGE_BASE_PATH}page-semantic.png`,
    actionVideo: `${IMAGE_BASE_PATH}page-semantic-action.mp4`
  },
  'customer-intent': { 
    avatar: `${IMAGE_BASE_PATH}customer-intent.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}customer-intent-action.mp4`
  },
  'question-suggestion': { 
    avatar: `${IMAGE_BASE_PATH}suggestions-with-answers.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}suggestions-with-answers-action.mp4`
  },
  'adjacent-topics': { 
    avatar: `${IMAGE_BASE_PATH}adjacent.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}adjacent-topics-action.mp4`
  },
  'auto-evaluator': { 
    avatar: `${IMAGE_BASE_PATH}auto-evaluator.jpg`, // Use the correct image from Meet the Crew
    actionVideo: `${IMAGE_BASE_PATH}auto-evaluator-action.mp4`
  },
  'competitive-analysis': { 
    avatar: `${IMAGE_BASE_PATH}competitors.png`,
    actionVideo: `${IMAGE_BASE_PATH}competitors-action.mp4`
  },
  'intent-based-recommender': { 
    avatar: `${IMAGE_BASE_PATH}content-recommender.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}content-generator-action.mp4`
  },
  'page-chatbot': { 
    avatar: `${IMAGE_BASE_PATH}chatbot.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}chatbot-action.mp4`
  },
  'narrative-presentation': { 
    avatar: `${IMAGE_BASE_PATH}narrator.png`, 
    actionVideo: `${IMAGE_BASE_PATH}narrator-action.mp4`
  },
  'site-crawler': { 
    avatar: `${IMAGE_BASE_PATH}crawler.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}crawler-action.mp4`
  },
  'site-crawler-agent': { 
    avatar: `${IMAGE_BASE_PATH}crawler.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}crawler-action.mp4`
  },
  'style-suggestion': { 
    avatar: `${IMAGE_BASE_PATH}style-suggestion.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}style-suggestion-action.mp4`
  },
  'voice-assistant': { 
    avatar: `${IMAGE_BASE_PATH}voicebot.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}voicebot-action.mp4`
  },
  'voice-chatbot': { 
    avatar: `${IMAGE_BASE_PATH}voicebot.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}voicebot-action.mp4`
  },
  
  // Mappings for agents with -agent suffix in database
  'page-role-agent': { 
    avatar: `${IMAGE_BASE_PATH}page-role.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}page-role-action.mp4`
  },
  'page-semantic-intent-agent': { 
    avatar: `${IMAGE_BASE_PATH}page-semantic.png`,
    actionVideo: `${IMAGE_BASE_PATH}page-semantic-action.mp4`
  },
  'customer-intent-agent': { 
    avatar: `${IMAGE_BASE_PATH}customer-intent.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}customer-intent-action.mp4`
  },
  'page-chatbot-agent': { 
    avatar: `${IMAGE_BASE_PATH}chatbot.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}chatbot-action.mp4`
  },
  'voice-assistant-agent': { 
    avatar: `${IMAGE_BASE_PATH}voicebot.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}voicebot-action.mp4`
  },
  'content-analysis-agent': { 
    avatar: `${IMAGE_BASE_PATH}content-generator.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}content-generator-action.mp4`
  },
  'analytics-insight-agent': { 
    avatar: `${IMAGE_BASE_PATH}webanalytics.png`,
    actionVideo: `${IMAGE_BASE_PATH}webanalytics-action.mp4`
  },
  'marketing-optimization-agent': { 
    avatar: `${IMAGE_BASE_PATH}content-recommender.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}content-generator-action.mp4`
  },
  'decision-support-agent': { 
    avatar: `${IMAGE_BASE_PATH}benchmark.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}benchmark-action.mp4`
  },
  'form-understanding-agent': { 
    avatar: `${IMAGE_BASE_PATH}intent-checker.png`,
    actionVideo: `${IMAGE_BASE_PATH}intent-checker-action.mp4`
  },
  'dynamic-interaction-agent': { 
    avatar: `${IMAGE_BASE_PATH}handsoff.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}handsoff-action.mp4`
  },
  'task-execution-agent': { 
    avatar: `${IMAGE_BASE_PATH}benchmark.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}benchmark-action.mp4`
  },
  'multi-page-analysis-agent': { 
    avatar: `${IMAGE_BASE_PATH}sector-topic-generator.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}sector-topic-generator-action.mp4`
  },
  
  // Missing agents that were showing ui-avatars
  'orchestration': { 
    avatar: `${IMAGE_BASE_PATH}orchestration.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}orchestration-action.mp4`
  },
  'onboarding': { 
    avatar: `${IMAGE_BASE_PATH}onboarding.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}onboarding-action.mp4`
  },
  'site-sector-analyzer': { 
    avatar: `${IMAGE_BASE_PATH}site-sector-classifier.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}site-sector-classifier-action.mp4`
  },
  'conductor': { 
    avatar: `${IMAGE_BASE_PATH}conductor-agent.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}conductor-agent-action.mp4`
  },
  'insights-hub': { 
    avatar: `${IMAGE_BASE_PATH}insight-hub.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}insight-hub-action.mp4`
  },
  'session-summarizer': { 
    avatar: `${IMAGE_BASE_PATH}session-summarizer.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}session-summarizer-action.mp4`
  },
  'insights-hub-agent': { 
    avatar: `${IMAGE_BASE_PATH}insight-hub.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}insight-hub-action.mp4`
  },
  'session-summarizer-agent': { 
    avatar: `${IMAGE_BASE_PATH}session-summarizer.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}session-summarizer-action.mp4`
  },
  'orchestration-agent': { 
    avatar: `${IMAGE_BASE_PATH}site-sector-classifier.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}site-sector-classifier-action.mp4`
  },
  'topic-research': { 
    avatar: `${IMAGE_BASE_PATH}topic-research.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}topic-research-action.mp4`
  },
  
  // MISSING AGENTS - Need images/videos
  'product-comparison': { 
    avatar: `${IMAGE_BASE_PATH}product-comparison.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}product-comparison-action.mp4`
  },
  'customer-surveys': { 
    avatar: `${IMAGE_BASE_PATH}kyc-survey.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}kyc-survey-action.mp4`
  },
  'replay-analyst': { 
    avatar: `${IMAGE_BASE_PATH}replay-analyst.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}replay-analyst-action.mp4`
  },
  'behavior-analyst': { 
    avatar: `${IMAGE_BASE_PATH}behavior-analyst.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}behavior-analyst-action.mp4`
  },
  'sector-assigner': { 
    avatar: `${IMAGE_BASE_PATH}sector-assigner.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}sector-assigner-action.mp4`
  },
  
  // NEW AGENT MEDIA
  'adjacent-topics-suggestor': { 
    avatar: `${IMAGE_BASE_PATH}adjacent-topics-suggestor.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}adjacent-topics-suggestor-action.mp4`
  },
  'growth-analyst': { 
    avatar: `${IMAGE_BASE_PATH}growth-analyst.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}growth-analyst-action.mp4`
  },
  'segment-research': { 
    avatar: `${IMAGE_BASE_PATH}segment-research-agent.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}segment-research-agent-action.mp4`
  },
  'customer-segmentation': { 
    avatar: `${IMAGE_BASE_PATH}customer-segmentation-agent.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}customer-segmentation-agent-action.mp4`
  },
  'sector-topic-generator': { 
    avatar: `${IMAGE_BASE_PATH}sector-topic-generator.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}sector-topic-generator-action.mp4`
  },
  'site-sector-classifier': { 
    avatar: `${IMAGE_BASE_PATH}site-sector-classifier.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}site-sector-classifier-action.mp4`
  },
  
  // Additional mappings for agents in the database
  'usability-insight': { 
    avatar: `${IMAGE_BASE_PATH}handsoff.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}handsoff-action.mp4`
  },
  'seo-performance': { 
    avatar: `${IMAGE_BASE_PATH}webanalytics.png`,
    actionVideo: `${IMAGE_BASE_PATH}webanalytics-action.mp4`
  },
  'lead-capture': { 
    avatar: `${IMAGE_BASE_PATH}suggestions-with-answers.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}suggestions-with-answers-action.mp4`
  },
  'experiment-analyst': { 
    avatar: `${IMAGE_BASE_PATH}benchmark.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}benchmark-action.mp4`
  },
  'automatic-survey': { 
    avatar: `${IMAGE_BASE_PATH}narrator.png`,
    actionVideo: `${IMAGE_BASE_PATH}narrator-action.mp4`
  },
  'autonomous-layout': { 
    avatar: `${IMAGE_BASE_PATH}handsoff.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}handsoff-action.mp4`
  },
  'intent': { 
    avatar: `${IMAGE_BASE_PATH}adjacent.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}adjacent-topics-action.mp4`
  },
  'pricing-optimization': { 
    avatar: `${IMAGE_BASE_PATH}content-recommender.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}content-generator-action.mp4`
  },
  'semantic-parsing': { 
    avatar: `${IMAGE_BASE_PATH}crawler.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}crawler-action.mp4`
  },
  
  // COMING SOON AGENTS
  'web-analyst': { 
    avatar: `${IMAGE_BASE_PATH}webanalytics.png`,
    actionVideo: `${IMAGE_BASE_PATH}webanalytics-action.mp4`
  },
  'counter-narrative': { 
    avatar: `${IMAGE_BASE_PATH}counter-narrative.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}counter-narrative-action.mp4`
  },
  'hands-off-navigation': { 
    avatar: `${IMAGE_BASE_PATH}handsoff.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}handsoff-action.mp4`
  },
  'bug-finder': { 
    avatar: `${IMAGE_BASE_PATH}intent-checker.png`,
    actionVideo: `${IMAGE_BASE_PATH}intent-checker-action.mp4`
  },
  
  // HIDDEN AGENTS
  'keyword-extractor': { 
    avatar: `${IMAGE_BASE_PATH}keyword-extractor.jpg`, // Use the correct image from Meet the Crew
    actionVideo: `${IMAGE_BASE_PATH}keyword-extractor-action.mp4`
  },
  'mock-page-chatbot': { 
    avatar: `${IMAGE_BASE_PATH}chatbot.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}chatbot-action.mp4`
  },
  'chatbot-voice': { 
    avatar: `${IMAGE_BASE_PATH}chatbot.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}chatbot-action.mp4`
  },
  // Chatbot Analytics Agents
  'chatbot-intent-classifier': {
    avatar: `${IMAGE_BASE_PATH}chatbot-intent-classifier.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}chatbot-intent-classifier-action.mp4`
  },
  'chatbot-quality-scorer': {
    avatar: `${IMAGE_BASE_PATH}chatbot-quality-scorer.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}chatbot-quality-scorer-action.mp4`
  },
  'chatbot-content-gap-detector': {
    avatar: `${IMAGE_BASE_PATH}chatbot-content-gap-detector.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}chatbot-content-gap-detector-action.mp4`
  },
  'chatbot-conversation-flow-analyzer': {
    avatar: `${IMAGE_BASE_PATH}chatbot-conversation-flow-analyzer.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}chatbot-conversation-flow-analyzer-action.mp4`
  },
  'chatbot-analytics-agent': {
    avatar: `${IMAGE_BASE_PATH}chatbot-analytics-agent.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}chatbot-analytics-agent-action.mp4`
  }
};

/**
 * Maps agent types to descriptive missions
 * Uses exact descriptions from Meet the Crew where available
 */
const agentTypeToMission: Record<string, string> = {
  // IMPLEMENTED AGENTS
  'page-role': 'Analyzes site structure to optimize semantic organization and SEO performance',
  'page-semantic-intent': 'Understands the structure, context, and meaning of page content',
  'customer-intent': 'Analyzes user behavior to understand purchase intent and motivations',
  'question-suggestion': 'Provides proactive suggestions with ready-to-use answers for common questions',
  'adjacent-topics': 'Identifies related topics to expand content reach and relevance',
  'auto-evaluator': 'Measures your website\'s performance against industry benchmarks',
  'competitive-analysis': 'Tracks competitor websites and extracts comparative insights',
  'intent-based-recommender': 'Suggests the most relevant content to users based on their profile and journey',
  'page-chatbot': 'Engages users in conversational interactions to resolve queries and guide navigation',
  'narrative-presentation': 'Reads aloud page content to improve accessibility and engagement',
  'site-crawler': 'Scans the entire website to collect and update structured content',
  'style-suggestion': 'Analyzes website styles and suggests compatible design tokens',
  'voice-assistant': 'Provides voice-based interactions for site navigation and customer assistance',
  'voice-chatbot': 'Conducts natural voice conversations with personalized responses and context awareness',
  
  // HIDDEN AGENTS
  'keyword-extractor': 'Extracts key terms and phrases from content',
  'mock-page-chatbot': 'Simulates conversational interactions for web pages',
  
  // COMING SOON AGENTS
  'web-analyst': 'Monitors website traffic, user behavior, and engagement patterns to provide actionable insights',
  'counter-narrative': 'Detects misinformation and builds counter-narratives to protect brand reputation',
  'hands-off-navigation': 'Guides users through websites automatically without manual clicks',
  'bug-finder': 'Identifies usability issues and broken elements on your site',
  'topic-research': 'Conducts in-depth research on specific topics to generate comprehensive content insights',
  'product-comparison': 'Analyzes and compares products across your site to provide intelligent recommendations',
  'customer-surveys': 'Designs intelligent surveys to understand customer needs and preferences',
  'replay-analyst': 'Reviews user session replays to identify pain points and friction in the user journey',
  'behavior-analyst': 'Studies user behavior patterns to provide actionable insights for optimization',
  'sector-assigner': 'Analyzes site content to automatically assign appropriate business sectors',
  'adjacent-topics-suggestor': 'Suggests related topics to customers to enhance their journey and discovery',
  'growth-analyst': 'Analyzes growth metrics and provides insights to scale your business',
  'segment-research': 'Performs in-depth research on identified customer segments to uncover insights',
  'customer-segmentation': 'Transforms your customer base into actionable segments with AI-powered analysis',
  'sector-topic-generator': 'Generates relevant topics based on business sector analysis',
  'site-sector-classifier': 'Analyzes site content to automatically assign appropriate business sectors',
  'site-sector-analyzer': 'Analyzes site content to automatically assign appropriate business sectors',
  'insights-hub': 'Central hub that aggregates and synthesizes insights from all active agents',
  'session-summarizer': 'Analyzes completed chat conversations to create actionable summaries',
  // Chatbot Analytics Agents
  'chatbot-intent-classifier': 'Analyze chatbot messages using AI to classify user intents with semantic understanding',
  'chatbot-quality-scorer': 'Evaluate chatbot response quality using AI for relevance, helpfulness, and accuracy',
  'chatbot-content-gap-detector': 'Identify missing content and knowledge gaps in chatbot interactions',
  'chatbot-conversation-flow-analyzer': 'Map and analyze chatbot conversation patterns to optimize user journeys',
  'chatbot-analytics-agent': 'Orchestrate the entire chatbot analytics pipeline for comprehensive insights'
};

/**
 * Maps agent types to current development challenges
 * Based on the 'Care and Maintenance' and 'Potential Evolutions' from Meet the Crew
 */
const agentTypeToChallenge: Record<string, string> = {
  // IMPLEMENTED AGENTS
  'page-role': 'Improve accuracy in distinguishing similar page types',
  'page-semantic-intent': 'Needs periodic retraining as site content evolves',
  'customer-intent': 'Needs regular input from web analytics and CRM',
  'question-suggestion': 'Needs regular update of answer database',
  'adjacent-topics': 'Requires monitoring of industry trends',
  'auto-evaluator': 'Needs updated benchmark datasets',
  'competitive-analysis': 'Needs competitor monitoring schedules',
  'intent-based-recommender': 'Needs access to user behavior data and content metadata',
  'page-chatbot': 'Requires training data updates and conversation design',
  'narrative-presentation': 'Needs updated text-to-speech content synchronization',
  'site-crawler': 'Requires crawl scheduling and exclusion rules',
  'style-suggestion': 'Improve accuracy in extracting design patterns',
  'voice-assistant': 'Needs voice command training and TTS tuning',
  'voice-chatbot': 'Needs integration with page context and conversation history management',
  
  // HIDDEN AGENTS
  'keyword-extractor': 'Better handling of industry-specific terminology',
  'mock-page-chatbot': 'Improve natural conversational flow',
  
  // COMING SOON
  'web-analyst': 'Requires integration with web analytics tools and continuous data refresh',
  'counter-narrative': 'Requires real-time monitoring of external content',
  'hands-off-navigation': 'Requires continuous mapping of site structure',
  'bug-finder': 'Requires regular site scans',
  'topic-research': 'Maintaining topic relevance and research quality across diverse industries',
  'product-comparison': 'Building comprehensive comparison frameworks',
  'customer-surveys': 'Creating contextual and non-intrusive survey experiences',
  'replay-analyst': 'Processing large volumes of session data efficiently',
  'behavior-analyst': 'Identifying meaningful patterns from complex behavioral data',
  'sector-assigner': 'Accurate sector classification across diverse industries',
  'adjacent-topics-suggestor': 'Balancing relevance with discovery for optimal user engagement',
  'growth-analyst': 'Providing actionable insights from complex growth metrics',
  'segment-research': 'Requires regular updates to research methodologies and continuous segment data analysis',
  'customer-segmentation': 'Requires regular customer data updates and segmentation model refinement',
  'sector-topic-generator': 'Maintaining topic quality and relevance across different sectors',
  'site-sector-classifier': 'Requires sector taxonomy updates',
  'site-sector-analyzer': 'Requires sector taxonomy updates',
  'insights-hub': 'Requires regular synchronization with all agent outputs',
  'session-summarizer': 'Monitor OpenAI API usage, ensure timely processing of chat sessions',
  
  // Chatbot Analytics Agents
  'chatbot-intent-classifier': 'Improve multi-intent detection and industry-specific models',
  'chatbot-quality-scorer': 'Develop domain-specific quality models and sentiment-aware scoring',
  'chatbot-content-gap-detector': 'Automate content generation for identified gaps',
  'chatbot-conversation-flow-analyzer': 'Implement real-time flow guidance and personalized paths',
  'chatbot-analytics-agent': 'Enable real-time stream processing and self-healing pipelines'
};

/**
 * Create a mock agent from just an agent type
 * Extracted as a standalone function for direct use
 */

/**
 * Service for interacting with agent data
 * Maps between the legacy backend API and our new agent model
 */

export const agentService = {
  /**
   * Create a mock agent from just an agent type
   * Available as a method for direct use
   */
  
  /**
   * Determine the standard agent type from an agent object
   * Maps MongoDB agent objects to standard agent types 
   * Exported for use in other components
   */
  determineAgentType(agent: any): string {
    // If agent has an explicit agent_type field, use that
    if (agent.agent_type) {
      return agent.agent_type;
    }

    // If agent has a name field, try to map it to a standard type
    if (agent.name) {
      const name = agent.name.toLowerCase();
      
      // Map common names to agent types
      if (name.includes('page semantic') || name.includes('semantic')) {
        return 'page-semantic-intent';
      }
      
      if (name.includes('page role') || name.includes('role')) {
        return 'page-role';
      }
      
      if (name.includes('customer intent') || name.includes('intent')) {
        return 'customer-intent';
      }
      
      if (name.includes('chatbot') || name.includes('chat')) {
        return 'page-chatbot';
      }
      
      if (name.includes('voice')) {
        return 'voice-assistant';
      }
    }
    
    // Use the category field to determine a likely agent type
    if (agent.category) {
      switch (agent.category.toLowerCase()) {
        case 'seo':
          return 'page-semantic-intent';
        case 'content':
          return 'adjacent-topics';
        case 'intent':
          return 'customer-intent';
        case 'support':
          return 'page-chatbot';
        case 'research':
          return 'competitive-analysis';
        case 'analytics':
          return 'auto-evaluator';
      }
    }
    
    // If we couldn't determine the type, return page-role as a default
    return 'page-role';
  },
  /**
   * Check if agent has voice capabilities
   */
  hasVoiceCapabilities(agent: Agent | null | undefined): boolean {
    if (!agent) return false;
    // By type, never by _id: recorded rows carry a uuid there (codex #6323 round 4).
    const type = agent.canonicalType ?? agent.agent_type ?? agent._id;
    const canonical = AGENT_ID_MAPPING[type] || type;
    return canonical === 'voice-assistant' || canonical === 'voice-chatbot';
  },
  
  /**
   * Get voice settings for an agent
   */
  async getVoiceSettings(agentId: string): Promise<VoiceSettings> {
    debugLog(`Fetching voice settings for agent ${agentId}`);
    try {
      // In a real implementation, we would fetch from the API
      // For now, return default settings
      return {
        voiceProvider: 'openai',
        voice: 'nova',
        language: 'en',
        model: 'gpt-4o',
      };
    } catch (error) {
      console.error(`Error fetching voice settings for agent ${agentId}:`, error);
      
      // Return default settings
      return {
        voiceProvider: 'openai',
        voice: 'nova',
        language: 'en',
        model: 'gpt-4o',
      };
    }
  },
  
  /**
   * Update voice settings
   */
  async updateVoiceSettings(agentId: string, settings: VoiceSettings): Promise<boolean> {
    debugLog(`Updating voice settings for agent ${agentId}`, settings);
    try {
      // In a real implementation, we would save to the API
      // For now, just return success
      return true;
    } catch (error) {
      console.error(`Error updating voice settings for agent ${agentId}:`, error);
      return false;
    }
  },
  
  /**
   * Get WebSocket URL for voice assistant
   */
  async getVoiceAssistantWsUrl(): Promise<string> {
    try {
      // Fetch from API
      const response = await fetch(`${API_BASE_URL}/voice-assistant/ws-url`);
      if (response.ok) {
        const data = await response.json();
        return data.url;
      }
    } catch (error) {
      console.error('Error fetching WebSocket URL:', error);
    }
    
    // Fallback to default if API call fails - use dynamic URL based on current host
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${protocol}//${host}/ws/voice-assistant`;
  },
  
  /**
   * Get voice assistant service status
   */
  async getVoiceAssistantStatus(): Promise<{ status: 'online' | 'offline' | 'degraded', message: string }> {
    try {
      // Fetch from API
      const response = await fetch(`${API_BASE_URL}/voice-assistant/status`);
      if (response.ok) {
        const data = await response.json();
        return {
          status: data.status,
          message: data.message
        };
      }
    } catch (error) {
      console.error('Error fetching voice assistant status:', error);
    }
    
    // Fallback if API call fails
    return {
      status: 'offline',
      message: 'Could not connect to voice assistant service'
    };
  },
  /**
   * Fetch all agents
   * @param includeComingSoon Whether to include "coming soon" agents
   * @param includeHidden Whether to include hidden agents
   */
  async getAgents(includeComingSoon: boolean = true, includeHidden: boolean = false): Promise<Agent[]> {
    // GET /api/agents (routes/agents.js:120): the agents table rows. A failure THROWS;
    // the previous fallback built the whole list from AGENT_ORDER as mock agents, and
    // because it fetched without the /api prefix, production always took that path.
    const url = `${API_URL}/agents`;
    const response = await authFetch(url);
    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`GET ${url} returned HTTP ${response.status}${body ? `: ${body.slice(0, 300)}` : ''}`);
    }
    const data = await response.json();
    if (!data || !Array.isArray(data.agents)) throw new Error(`GET ${url} returned no agents array in its body`);
    const mapped: Agent[] = data.agents.map((agent: any) => this.mapToNewAgentModel(agent));

    // Static product classification (which id is canonical, which agents are hidden
    // or coming soon, display order) is applied to the recorded rows; it invents no value.
    // Three identities, none rewritten into another (codex #6323 rounds 2-3):
    //   _id            the row uuid, what settings and status writes address;
    //   agent_type     the RECORDED type, the key agent_logs and the statistics summary use;
    //   canonicalType  the CLASSIFICATION identity (alias → canonical), for hidden /
    //                  coming-soon / duplicates / order only.
    // The summary route groups by the recorded type and folds no aliases, so neither does
    // the join here: an alias-backed row joins the statistics recorded under its alias.
    const recordedType = (agent: Agent) => agent.agent_type ?? agent._id;
    const canonicalOf = (agent: Agent) => agent.canonicalType ?? recordedType(agent);
    const normalizedAgents = new Map<string, Agent>();
    for (const agent of mapped) {
      const type = recordedType(agent);
      const canonicalType = AGENT_ID_MAPPING[type] || type;
      if (type === 'voice-chatbot' || type === 'voice-chatbot-agent') continue; // voice-assistant is the canonical row
      // Two rows of one canonical type (an alias row beside the canonical one): the first recorded wins.
      if (!normalizedAgents.has(canonicalType)) normalizedAgents.set(canonicalType, { ...agent, canonicalType });
    }
    const filtered = Array.from(normalizedAgents.values()).filter((agent) => {
      const info = agentTypeToCategory[canonicalOf(agent)];
      if (!info) return true;
      if (info.status === 'hidden' && !includeHidden) return false;
      if (info.status === 'coming-soon' && !includeComingSoon) return false;
      return true;
    });
    return filtered.sort((a, b) => {
      const ia = AGENT_ORDER.indexOf(canonicalOf(a)); const ib = AGENT_ORDER.indexOf(canonicalOf(b));
      if (ia === -1 && ib === -1) return a.name.localeCompare(b.name);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  },
  
  /**
   * Success rate over TERMINAL executions only (succeeded / (succeeded + failed)), as a
   * percentage; null when nothing terminal was recorded. Pending, processing and
   * unknown statuses are neither successes nor failures and are reported beside it.
   * The one definition the card and the drawer share.
   */
  successRateOf(s: AgentStatsSummary | null | undefined): number | null {
    if (!s) return null;
    const terminal = s.succeeded + s.failed;
    return terminal > 0 ? (s.succeeded / terminal) * 100 : null;
  },

  /**
   * Per-agent execution statistics measured from agent_logs: GET /api/logs/stats/summary
   * (routes/logs-postgresql.js:407) groups rows by agent_type and status with a count,
   * the average duration_ms and the latest created_at. Nothing is estimated: an agent
   * with no rows is simply absent from the map, and an average over rows that recorded
   * no duration is null.
   */
  async getAgentStatsSummary(): Promise<Record<string, AgentStatsSummary>> {
    const url = `${API_URL}/logs/stats/summary`;
    const response = await authFetch(url);
    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`GET ${url} returned HTTP ${response.status}${body ? `: ${body.slice(0, 300)}` : ''}`);
    }
    const data = await response.json();
    if (!data || !Array.isArray(data.stats)) throw new Error(`GET ${url} returned no stats array in its body`);
    const out: Record<string, AgentStatsSummary> = {};
    const num = (v: unknown, what: string) => {
      const n = Number(v);
      if (v === null || v === undefined || !Number.isFinite(n)) throw new Error(`GET ${url}: ${what} is ${JSON.stringify(v)}, not a number`);
      return n;
    };
    for (const row of data.stats as Array<Record<string, unknown>>) {
      const type = String(row.agent_type ?? '');
      if (!type) continue;
      const group = `${type}/${String(row.status)}`;
      const count = num(row.count, `count for ${group}`);
      const cur = out[type] ?? { agentType: type, executions: 0, succeeded: 0, failed: 0, nonterminal: 0, unknown: 0, durationSamples: 0, durationMsTotal: 0, avgDurationMs: null, lastAt: null };
      cur.executions += count;
      // Every status goes through the classification table; nothing is counted as a
      // success by default (pending, processing and unrecognised words are not successes).
      const cls = classifyExecutionStatus(row.status).cls;
      if (cls === 'success') cur.succeeded += count;
      else if (cls === 'failure') cur.failed += count;
      else if (cls === 'nonterminal') cur.nonterminal += count;
      else cur.unknown += count;
      // AVG(duration_ms) ignores null durations, so the weight is COUNT(duration_ms), the
      // rows that carry one, never the group's row count.
      if (row.avg_duration !== null && row.avg_duration !== undefined) {
        const avg = num(row.avg_duration, `avg_duration for ${group}`);
        const withDuration = num(row.duration_count, `duration_count for ${group}`);
        cur.durationSamples += withDuration; cur.durationMsTotal += avg * withDuration;
      }
      const last = typeof row.last_at === 'string' ? row.last_at : null;
      if (last && (!cur.lastAt || last > cur.lastAt)) cur.lastAt = last;
      out[type] = cur;
    }
    for (const s of Object.values(out)) s.avgDurationMs = s.durationSamples > 0 ? s.durationMsTotal / s.durationSamples : null;
    return out;
  },

  /**
   * Fetch a specific agent by ID
   */
  async getAgentById(id: string): Promise<Agent | null> {
    // A row uuid goes to GET /api/agents/:id; anything else (an agent_type) is looked up
    // in the recorded list. Not found is null; a failure THROWS. The previous chain ended
    // in a bypass mock and an invented placeholder row.
    if (!id) throw new Error('getAgentById: an id is required');
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
      const url = `${API_URL}/agents/${encodeURIComponent(id)}`;
      const response = await authFetch(url);
      if (response.status === 404) return null;
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new Error(`GET ${url} returned HTTP ${response.status}${body ? `: ${body.slice(0, 300)}` : ''}`);
      }
      // routes/agents.js:202 answers with the document itself, not a wrapper.
      const doc = await response.json();
      if (!doc || typeof doc !== 'object' || Array.isArray(doc) || !(doc.id || doc._id || doc.agent_type)) {
        throw new Error(`GET ${url} returned no agent document in its body`);
      }
      return this.mapToNewAgentModel(doc);
    }
    const all = await this.getAgents(true, true);
    return all.find((agent) => agent._id === id || agent.agent_type === id || agent.canonicalType === id || this.determineAgentType(agent) === id) ?? null;
  },
  
  /**
   * Fetch an agent by type (agent_type)
   */
  async getAgentByType(agentType: string): Promise<Agent | null> {
    const all = await this.getAgents(true, true);
    return all.find((agent) => agent._id === agentType || agent.agent_type === agentType) ?? null;
  },
  
  /**
   * Update an agent's status (active/paused)
   */
  async updateAgentStatus(agentId: string, status: 'active' | 'paused'): Promise<void> {
    // PUT /api/agents/:id (routes/agents.js:240, admin only) writes the agents.status
    // column. The previous code POSTed to /agents/:id/hire and /fire, routes that do not
    // exist, and returned false silently. A failure THROWS with status and body.
    if (!agentId) throw new Error('updateAgentStatus: an agent id is required');
    const url = `${API_URL}/agents/${encodeURIComponent(agentId)}`;
    const response = await authFetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`PUT ${url} returned HTTP ${response.status}${body ? `: ${body.slice(0, 300)}` : ''}`);
    }
  },
  
  /**
   * Execution logs for one agent type, mapped row by row. A failure THROWS: an
   * empty list is only ever the API's own answer, never a substitute for an error.
   */
  async getAgentLogs(agentType: string, limit: number = 20, offset: number = 0): Promise<ExecutionLog[]> {
    debugLog(`Fetching logs for agent ${agentType}`);
    const logsResult = await agentApiFixed.getLogs({ agentType, limit, offset });
    return logsResult.logs.map((log) => this.mapToExecutionLog(agentApiFixed.normalizeAgentLogRow(log)));
  },
  
  /**
   * One agent_logs row by its TRACE id (GET /api/logs/by-trace/:traceId, auth
   * token). The identifier kind is explicit: 2,307 rows on db2 carry a UUID-shaped
   * trace id that differs from the row uuid, so a shape test cannot pick the route.
   * 404 THROWS (the row is not there); no mock, no bypass leg, no finally-return.
   */
  async getLogByTraceId(traceId: string): Promise<ExecutionLog> {
    const row = await agentApiFixed.getLogByTraceId(traceId);
    if (!row) throw new Error(`GET /api/logs/by-trace/${traceId} returned HTTP 404: no log with that trace id`);
    // The route returns an agent_logs row or an agent_test_runs row; one normaliser for both.
    return this.mapToExecutionLog(agentApiFixed.normalizeAgentLogRow(row));
  },

  /**
   * One agent_logs row by its ROW uuid (GET /api/logs/:id, auth token). Only for a
   * row that has no trace id; a trace id goes through getLogByTraceId. A non-uuid
   * THROWS rather than being guessed at; a miss or failure THROWS with status + body.
   */
  async getLogById(rowId: string): Promise<ExecutionLog> {
    if (!rowId) throw new Error('getLogById: the row carries no id');
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(rowId)) {
      throw new Error(`getLogById expects a row uuid, got "${rowId}"; a trace id goes through getLogByTraceId`);
    }
    const url = `${API_URL}/logs/${rowId}`;
    const response = await authFetch(url);
    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`GET ${url} returned HTTP ${response.status}${body ? `: ${body.slice(0, 300)}` : ''}`);
    }
    const data = await response.json();
    if (!data || !data.log) throw new Error(`GET ${url} returned no log in its body`);
    return this.mapToExecutionLog(agentApiFixed.normalizeAgentLogRow(data.log));
  },
  /**
   * Format an informative async response message
   */
  formatAsyncResponseMessage(result: any, agentType: string): string {
    const traceId = result.trace_id;
    const estimatedTime = agentType ? this.getEstimatedProcessingTime(agentType) : 20;
    
    return `Your test is being processed asynchronously via Kafka.

Trace ID: ${traceId}
Agent Type: ${agentType}
Status: Processing
Estimated completion time: ${estimatedTime} seconds

The request has been successfully forwarded to the real agent service. Results will be available in the logs when processing is complete.
To check the status manually, go to the Logs page and look for logs with trace ID: ${traceId}`;
  },
  
  /**
   * Get estimated processing time based on agent type
   */
  getEstimatedProcessingTime(agentType: string): number {
    switch (agentType) {
      case 'page-semantic-intent':
      case 'page-role':
        return 15; // Semantic analysis tends to be faster
      case 'customer-intent':
        return 20;
      case 'page-chatbot':
      case 'voice-chatbot':
        return 25; // Chat responses need more contextual understanding
      case 'intent-based-recommender':
        return 30; // Recommendation engines can take longer
      default:
        return 20; // Default estimate
    }
  },
  
  /*
   * rateAgentExecution was removed in task-flqblaa1r2. It forwarded to
   * agentApiFixed.rateAgentExecution, which was a mock returning { success: true } after a
   * Math.random sleep -- no HTTP call, no route behind it, and no agent_ratings table. Every
   * caller therefore reported a submission that never happened. There is nothing to restore
   * it to until a rating endpoint exists.
   */

  /**
   * Get usage statistics for an agent
   */
  
  /**
   * Map the legacy agent model to our new Agent model
   */
  mapToNewAgentModel(legacyAgent: any): Agent {
    // Properly determine the agent type using our determineAgentType function
    const agentType = this.determineAgentType(legacyAgent);
    const agentId = legacyAgent._id || agentType;
    
    // Use the determined agent type for lookups
    const mappingInfo = agentTypeToCategory[agentType] || {
      category: 'Research',
      color: 'blue',
      maturity: 'Rookie',
      status: 'available'
    };
    
    const mediaInfo = agentTypeToMedia[agentType] || {
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(agentType)}&background=random`,
      actionVideo: ''
    };
    
    // Special handling for voice agents to ensure they have distinct avatars and videos
    if (agentId === 'voice-chatbot') {
      debugLog('Mapping Voice Chatbot with distinct identity');
    }
    
    return {
      _id: agentId,
      agent_type: agentType,
      name: legacyAgent.name || mappingInfo.displayName || agentType.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
      avatar: legacyAgent.config?.avatar || mediaInfo.avatar,
      actionVideo: legacyAgent.config?.actionVideo || mediaInfo.actionVideo,
      mission: legacyAgent.description || agentTypeToMission[agentType] || `Agent for ${agentType}`,
      maturity: mappingInfo.maturity,
      // Recorded values only. progress, performanceData, performanceChange and
      // findingsCount have no source in the agents table or any log; they were random
      // numbers per render and are gone. Dates are the row's own columns or absent.
      challenge: legacyAgent.challenge || agentTypeToChallenge[agentType],
      category: mappingInfo.category,
      color: mappingInfo.color,
      status: typeof legacyAgent.status === 'string' && legacyAgent.status !== '' ? legacyAgent.status : 'unknown',
      createdAt: typeof legacyAgent.created_at === 'string' ? legacyAgent.created_at : typeof legacyAgent.createdAt === 'string' ? legacyAgent.createdAt : undefined,
      // The row's modification time is when it was last UPDATED (a pause, a settings edit),
      // not when the agent last ran; activity is the log summary's lastAt (codex #6323 round 4).
      updatedAt: typeof legacyAgent.updated_at === 'string' ? legacyAgent.updated_at : typeof legacyAgent.updatedAt === 'string' ? legacyAgent.updatedAt : undefined,
      source: legacyAgent.source || 'PageMind',
      marketplaceStatus: mappingInfo.status || 'available'
    };
  },
  
  /**
   * Map an agent_logs row (either field spelling) to ExecutionLog. Absent stays
   * absent: duration and tokenUsage are null when the row recorded none, status is
   * the row's own value, id and trace id are the row's or empty. Nothing is invented.
   * Measured on db2 2026-09-17: of 291,665 rows, 61 carry duration, 7,628 duration_ms,
   * 5,404 metadata.tokens_used; status is completed on 99% of them.
   */
  mapToExecutionLog(agentLog: AgentLog & AgentLogRowFields): ExecutionLog {
    // Seconds. The normaliser folds every recorded duration column (all milliseconds, see
    // DURATION_COLUMN_TABLE) into duration_ms; this division is the one unit conversion.
    // timing_breakdown.total now has NO producer at all: its only one was routes/bypass.js, the mock
    // retired in task-1urp0gi3tm. It is not read here either.
    const duration = typeof agentLog.duration_ms === 'number' ? agentLog.duration_ms / 1000 : null;
    const tokenUsage = typeof agentLog.metadata?.tokens_used === 'number' ? agentLog.metadata.tokens_used : null;

    let timestamp = '';
    if (agentLog.timestamp instanceof Date) {
      timestamp = agentLog.timestamp.toISOString();
    } else if (agentLog.timestamp != null && agentLog.timestamp !== '') {
      const d = new Date(agentLog.timestamp);
      timestamp = Number.isNaN(d.getTime()) ? String(agentLog.timestamp) : d.toISOString();
    }

    return {
      id: agentLog._id || agentLog.id || agentLog.trace_id || '',
      timestamp,
      inputType: this.determineInputType(agentLog.input),
      duration,
      tokenUsage,
      status: agentLog.status || 'unknown',
      error: agentLog.error ?? null,
      traceId: agentLog.traceId || agentLog.trace_id || undefined,
      agent_type: agentLog.agent_type || undefined,
      // The row is normalised: input and the two outputs are already the first recorded
      // column, so no raw column (payload, response, result) is read here.
      input: agentLog.input,
      output: agentLog.parsed_output ?? agentLog.raw_output,
      metadata: agentLog.metadata,
      raw_output: agentLog.raw_output,
      parsed_output: agentLog.parsed_output,
      timing_breakdown: agentLog.timing_breakdown,
      prompt: agentLog.prompt,
      response: agentLog.raw_output
    };
  },
  
  /**
   * Determine the input type from the agent log input
   */
  determineInputType(input: any): string {
    if (!input) return 'Text';
    
    try {
      if (input.imagePath) return 'Image';
      if (input.customerMessage) return 'Chat';
      if (input.pageContent) return 'Page';
    } catch (error) {
      console.warn('Error determining input type:', error);
    }
    
    return 'Text';
  },

  /**
   * GET /api/agents/:id/settings, returned field by field as the API sent it, null
   * where it sent nothing. A non-ok response THROWS with status and body. No
   * defaults: a default here reads as the saved configuration.
   */
  async getAgentSettings(agentId: string): Promise<AgentSettings> {
    debugLog(`Fetching settings for agent ${agentId}`);
    const url = `${API_URL}/agents/${agentId}/settings`;
    const response = await authFetch(url);
    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`GET ${url} returned HTTP ${response.status}${body ? `: ${body.slice(0, 300)}` : ''}`);
    }
    const data = await response.json();
    const s = data?.settings;
    if (!s) throw new Error(`GET ${url} returned no settings in its body`);
    // The route sends a number or null per numeric field (backend-merged/utils/
    // agent-settings-shape.js numericValue); anything else is a contract defect and is
    // REFUSED, not coerced.
    const num = (field: string, v: unknown): number | null => {
      if (v === null || v === undefined) return null;
      if (typeof v === 'number' && Number.isFinite(v)) return v;
      throw new Error(`GET ${url} returned ${field} ${JSON.stringify(v)} (${typeof v}); expected a number or null`);
    };
    return {
      prompt: typeof s.prompt === 'string' ? s.prompt : null,
      model: typeof s.model === 'string' && s.model !== '' ? s.model : null,
      tokenLimit: num('tokenLimit', s.tokenLimit),
      temperature: num('temperature', s.temperature),
      topP: num('topP', s.topP),
      maxLength: num('maxLength', s.maxLength),
      conflicts: Array.isArray(s.conflicts) ? s.conflicts : [],
      invalid: Array.isArray(s.invalid) ? s.invalid : [],
    };
  },

  /**
   * PUT /api/agents/:id/settings with the auth token (the route requires it; the
   * previous raw fetch could only ever 401). A non-ok response THROWS with status.
   */
  async saveAgentSettings(agentId: string, settings: AgentSettings): Promise<void> {
    debugLog(`Saving agent settings for ${agentId}`, settings);
    const url = `${API_URL}/agents/${agentId}/settings`;
    // conflicts and invalid are API reports, not settings; the PUT body carries the fields the route writes.
    const { conflicts: _reported, invalid: _invalid, ...body } = settings;
    const response = await authFetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`PUT ${url} returned HTTP ${response.status}${body ? `: ${body.slice(0, 300)}` : ''}`);
    }
  },

  /**
   * Test an agent with given input
   */
  async testAgent(agentType: string, testInput: any): Promise<any> {
    debugLog(`Testing agent ${agentType}`, testInput);
    try {
      // Generate a trace ID for this test
      const traceId = `trace-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      
      // Prepare the test payload
      const payload = {
        agent_type: agentType,
        trace_id: traceId,
        event: {
          payload: testInput
        },
        model_config: {
          model: testInput.model || 'gpt-4o',
          temperature: testInput.temperature || 0.7,
          max_tokens: testInput.maxTokens || 2000
        }
      };
      
      debugLog(`Sending test request to /api/agents-test/test-run`, payload);
      
      const response = await fetch(`${API_BASE_URL}/agents-test/test-run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer test-token'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Test failed with status ${response.status}: ${errorText}`);
        throw new Error(`Test failed: ${response.status}`);
      }
      
      const result = await response.json();
      debugLog(`Test result:`, result);
      
      // Return the result with the trace ID and other relevant data
      return {
        success: result.success,
        traceId: result.trace_id || traceId,
        output: result.result || result.log?.parsed_output,
        prompt: result.log?.prompt,
        timing: {
          total: result.processing_time_ms || result.log?.duration || 0,
          database: 0,
          rag: 0,
          llm: result.processing_time_ms || result.log?.duration || 0
        },
        tokenUsage: result.log?.metadata?.tokens_used || 0,
        raw: result
      };
    } catch (error) {
      console.error(`Error testing agent ${agentType}:`, error);
      throw error;
    }
  }
};